import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { inrFull } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LotCard } from "@/components/LotCard";
import { SUPPORT_PHONE, SUPPORT_PHONE_TEL } from "@/lib/config";
import { Handshake, ArrowRight, Heart, Phone, X } from "@phosphor-icons/react";

const STATUS_STYLE = {
  new: "bg-navy/10 text-navy", contacted: "bg-warn/15 text-warn", negotiating: "bg-brand/15 text-brand",
  won: "bg-ok/15 text-ok", lost: "bg-destructive/10 text-destructive", closed: "bg-navy/10 text-navy",
};

export default function BuyerAccount() {
  const { user } = useAuth();
  const [eois, setEois] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/buyer/eois").then(({ data }) => setEois(data.eois)).catch(() => {}),
      api.get("/buyer/shortlist").then(({ data }) => setShortlist(data.lots)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const removeShortlist = async (lotId) => {
    try {
      await api.post(`/buyer/shortlist/${lotId}`);
      setShortlist((s) => s.filter((l) => l.id !== lotId));
      toast.success("Removed from shortlist");
    } catch { toast.error("Could not update shortlist"); }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">My account</span>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-navy" data-testid="buyer-greeting">Hi, {user?.name?.split(" ")[0]}</h1>
      <p className="text-muted-foreground">{user?.company || "Buyer"} · {user?.email}</p>

      {/* Support banner */}
      <div className="mt-6 flex flex-col gap-3 rounded-xl border border-teal/30 bg-darkteal-50 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="buyer-contact-banner">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-darkteal/10"><Phone size={20} className="text-darkteal" /></span>
          <div>
            <div className="text-sm font-semibold text-ink">Talk to SparesHub about any lot</div>
            <p className="text-xs text-subtext">Our team helps with pricing, inspection and escrow. Keep the lot number handy when you call.</p>
          </div>
        </div>
        <a href={`tel:${SUPPORT_PHONE_TEL}`} className="stat inline-flex items-center justify-center gap-2 rounded-sm bg-darkteal px-4 py-2.5 text-sm font-bold text-white hover:bg-darkteal/90" data-testid="buyer-contact-phone"><Phone size={16} /> {SUPPORT_PHONE}</a>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-navy/20 border-t-brand" /></div>
      ) : (
        <>
          {/* Shortlist */}
          <div className="mt-10 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy"><Heart weight="fill" className="text-brand" /> Shortlisted lots</h2>
            <Link to="/lots" className="text-sm font-semibold text-navy hover:text-brand">Browse more lots →</Link>
          </div>
          {shortlist.length === 0 ? (
            <div className="mt-4 rounded-sm border border-dashed border-border p-10 text-center" data-testid="shortlist-empty">
              <Heart size={36} className="mx-auto text-navy/20" />
              <p className="mt-3 text-muted-foreground">No shortlisted lots yet. Tap the heart on any lot to save it here.</p>
            </div>
          ) : (
            <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shortlist.map((lot) => (
                <div key={lot.id} className="relative" data-testid={`shortlist-item-${lot.id}`}>
                  <button onClick={() => removeShortlist(lot.id)} className="absolute -right-2 -top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-brand text-white shadow-md ring-2 ring-white hover:bg-brand-hover" data-testid={`remove-shortlist-${lot.id}`} title="Remove from shortlist"><X size={14} /></button>
                  <LotCard lot={lot} />
                </div>
              ))}
            </div>
          )}

          {/* EOIs / quotes */}
          <div className="mt-12">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy"><Handshake className="text-navy" /> My quotes & interests</h2>
          </div>
          {eois.length === 0 ? (
            <div className="mt-4 rounded-sm border border-dashed border-border p-16 text-center">
              <Handshake size={40} className="mx-auto text-navy/20" />
              <p className="mt-4 font-display text-lg font-semibold text-navy">No interests yet</p>
              <p className="mt-1 text-muted-foreground">Find a lot you like and express interest — our team will follow up.</p>
              <Link to="/lots" className="mt-4 inline-flex items-center gap-1 rounded-sm bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-hover">Browse lots <ArrowRight /></Link>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {eois.map((e) => (
                <div key={e.id} className="rounded-sm border border-border bg-white p-5" data-testid={`buyer-eoi-${e.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="stat rounded-sm bg-navy-50 px-2 py-0.5 text-xs font-bold text-navy" data-testid={`eoi-lot-code-${e.id}`}>{e.lot_code}</span>
                        <span className={`rounded-sm px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${STATUS_STYLE[e.status] || "bg-navy/10 text-navy"}`}>{e.status}</span>
                      </div>
                      <Link to={`/lots/${e.lot_id}`} className="mt-1.5 block font-display font-semibold text-navy hover:text-brand">{e.lot_title}</Link>
                      <div className="mt-1 text-sm text-muted-foreground">{e.notes || "No notes"}</div>
                      <div className="mt-1 text-xs text-muted-foreground">Submitted {new Date(e.created_at).toLocaleDateString("en-IN")}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Your quote</div>
                      <div className="stat text-lg font-bold text-brand" data-testid={`eoi-quote-${e.id}`}>{e.indicative_price ? inrFull(e.indicative_price) : "—"}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
                    <Phone size={14} className="text-brand" /> Questions? Call SparesHub at
                    <a href={`tel:${SUPPORT_PHONE_TEL}`} className="stat font-semibold text-navy hover:text-brand">{SUPPORT_PHONE}</a>
                    — quote <span className="stat font-semibold text-navy">{e.lot_code}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
